import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { USER_ROLE_TYPES } from '@/constants/roles';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  requiredRole?: 'supervisor' | 'field-worker';
  requireAdmin?: boolean;
}

const KIOSK_PATH = '/attendance';

export const ProtectedRoute = ({ requiredRole, requireAdmin }: ProtectedRouteProps) => {
  const { isAuthenticated, user, userRole, kioskMode, _hasHydrated } = useAuthStore();
  const location = useLocation();

  // Show loading while Zustand is hydrating from localStorage
  if (!_hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1a2942] mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (kioskMode && location.pathname !== KIOSK_PATH) {
    return <Navigate to={KIOSK_PATH} replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <h2 className="text-2xl font-medium text-red-800 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You do not have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-[#f57c00] text-white py-2 px-4 rounded hover:bg-[#e06d00]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if(requireAdmin && user?.role_type_id !== USER_ROLE_TYPES.admin){
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};
